import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ContextLine,
  DateUI,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
  toast,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { RulePill } from "~/components/TransactionMonitoring/RulePill";
import { TransactionMonitoringEvaluationTargetCard } from "~/components/TransactionMonitoring/TransactionMonitoringEvaluationTargetCard";
import { TransactionMonitoringFindingSummaryTable } from "~/components/TransactionMonitoring/TransactionMonitoringFindingsSummaryTable";
import { graphql } from "~/gql/client";
import type { AdminTransactionMonitoringEvaluationDetailFragment } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/transaction-monitoring_/evaluations_/$evaluationId",
)({
  head: () => opsHead("Transaction Monitoring Evaluation"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment AdminTransactionMonitoringEvaluationDetail on TransactionMonitoringEvaluation {
    id
    shortRef
    ruleSet
    status
    startedAt
    completedAt
    evaluatedRuleIds
    target {
      ...TransactionMonitoringEvaluationTargetCardFields
    }
    findings {
      ...TransactionMonitoringFindingSummaryRow
    }
  }
`);

function View() {
  const { evaluationId } = Route.useParams();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminTransactionMonitoringEvaluationDetail($evaluationId: ID!) {
        node(id: $evaluationId) {
          ...AdminTransactionMonitoringEvaluationDetail
        }
      }
    `),
    {
      variables: {
        evaluationId,
      },
    },
  );

  if (
    !data.node ||
    data.node.__typename !== "TransactionMonitoringEvaluation"
  ) {
    throw new NotFoundError();
  }

  const evaluation = data.node;

  useOpsTitle(`Evaluation #${evaluation.shortRef}`);

  return (
    <AdminPageContainer>
      <div className="flex flex-col">
        <AdminPageHeader
          title={`Evaluation #${evaluation.shortRef}`}
          subtitle={<span className="font-mono">{evaluation.id}</span>}
          contextLine={
            <ContextLine
              current={`#${evaluation.shortRef}`}
              ancestors={[
                {
                  label: "Transaction Monitoring",
                  path: "/transaction-monitoring",
                },
                {
                  label: "Evaluations",
                  path: "/transaction-monitoring/evaluations",
                },
              ]}
            />
          }
          buttons={<RerunEvaluationAction evaluation={evaluation} />}
        />

        <div className="flex flex-row justify-between gap-6">
          <div className="flex w-full flex-col">
            <EvaluationDetailsCard evaluation={evaluation} />
          </div>
          <div className="flex w-full flex-col">
            <EvaluationFindingsCard evaluation={evaluation} />
          </div>
        </div>
      </div>
    </AdminPageContainer>
  );
}

function EvaluationDetailsCard({
  evaluation,
}: {
  evaluation: AdminTransactionMonitoringEvaluationDetailFragment;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
        <CardDescription>
          Rule set, target, timing, and evaluated rules for this run.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <DetailRow
          label="Rule set"
          value={
            <Txt variant="body">{prettyPrintEnum(evaluation.ruleSet)}</Txt>
          }
        />
        <DetailRow
          label="Status"
          value={<StatusUI status={evaluation.status} />}
        />
        <DetailRow
          label="Target"
          value={
            evaluation.target ? (
              <div className="w-fit">
                <TransactionMonitoringEvaluationTargetCard
                  target={evaluation.target}
                />
              </div>
            ) : (
              <Txt variant="body">-</Txt>
            )
          }
        />
        <DetailRow
          label="Started at"
          value={
            evaluation.startedAt ? (
              <DateUI date={evaluation.startedAt} variant="DATETIME_TZ" />
            ) : (
              <Txt variant="body">-</Txt>
            )
          }
        />
        <DetailRow
          label="Completed at"
          value={
            evaluation.completedAt ? (
              <DateUI date={evaluation.completedAt} variant="DATETIME_TZ" />
            ) : (
              <Txt variant="body">-</Txt>
            )
          }
        />
        <DetailRow
          label="Evaluated rules"
          value={
            evaluation.evaluatedRuleIds.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {evaluation.evaluatedRuleIds.map((ruleId) => (
                  <RulePill ruleId={ruleId} key={ruleId} />
                ))}
              </div>
            ) : (
              <Txt variant="body">-</Txt>
            )
          }
        />
      </CardContent>
    </Card>
  );
}

function EvaluationFindingsCard({
  evaluation,
}: {
  evaluation: AdminTransactionMonitoringEvaluationDetailFragment;
}) {
  const findings = evaluation.findings ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Findings</CardTitle>
        <CardDescription>
          Findings created from this evaluation run.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {findings.length > 0 ? (
          <TransactionMonitoringFindingSummaryTable data={findings} />
        ) : (
          <Txt variant="body">No findings created by this evaluation.</Txt>
        )}
      </CardContent>
    </Card>
  );
}

function RerunEvaluationAction({
  evaluation,
}: {
  evaluation: AdminTransactionMonitoringEvaluationDetailFragment;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rerunEvaluation, { loading }] = useMutation(
    graphql(`
      mutation AdminRerunTransactionMonitoringEvaluation($input: RerunTransactionMonitoringEvaluationInput!) {
        rerunTransactionMonitoringEvaluation(input: $input) {
          transactionMonitoringEvaluation {
            id
            shortRef
          }
        }
      }
    `),
  );

  const isActionAvailable =
    evaluation.status === "COMPLETE" || evaluation.status === "FAILED";

  const submitRerun = async () => {
    const defaultErrorMessage =
      "There was an error re-running the evaluation. Please try again later.";

    await rerunEvaluation({
      variables: {
        input: {
          id: evaluation.id,
        },
      },
      onCompleted: (data) => {
        const rerun =
          data.rerunTransactionMonitoringEvaluation
            ?.transactionMonitoringEvaluation;

        if (!rerun) {
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to re-run evaluation",
            description: defaultErrorMessage,
          });
          return;
        }

        toast({
          kind: "success",
          autoHide: true,
          title: "Evaluation re-run started",
          description: `Evaluation #${rerun.shortRef} has been created.`,
        });
        setOpen(false);
        void navigate({
          to: "/transaction-monitoring/evaluations/$evaluationId",
          params: { evaluationId: rerun.id },
        });
      },
      onError: (error) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to re-run evaluation",
          description: error.graphQLErrors[0]?.message ?? defaultErrorMessage,
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={!isActionAvailable}>
          Re-run
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Re-run evaluation</DialogTitle>
          <DialogDescription>
            This creates a new evaluation for the same rule set and target.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm">
          A new evaluation record will be created and you will be taken to that
          run once it has been queued.
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setOpen(false)}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => void submitRerun()}
            loading={loading}
          >
            Re-run evaluation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
